import React, { useState, useEffect } from 'react';
import { Building, Plus, Edit, Trash2, User, Eye, X, Search, Filter } from 'lucide-react';
import { logger } from './services/logger';

// Simulación de datos de empresas
const mockCompanies = [
  {
    id: 1,
    name: 'Empresa Demo',
    description: 'Empresa de demostración del sistema',
    status: 'active',
    adminUser: 'admin@empresademo.com',
    createdAt: '2024-01-15',
    usersCount: 12,
    templatesCount: 8
  },
  {
    id: 2,
    name: 'GeoConsulting Ltda',
    description: 'Consultora especializada en sistemas de información geográfica',
    status: 'active',
    adminUser: 'admin@geoconsulting.com',
    createdAt: '2024-02-20',
    usersCount: 5,
    templatesCount: 3
  },
  {
    id: 3,
    name: 'Cartografía Digital',
    description: 'Servicios de cartografía y mapeo digital',
    status: 'inactive',
    adminUser: 'admin@cartografia.com',
    createdAt: '2024-03-10',
    usersCount: 8,
    templatesCount: 15
  }
];

const AdminCompaniesModule = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'view'
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);

  // Formulario
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'active',
    adminUser: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // Hooks necesarios (se asume que están disponibles)
  // const { hasPermission } = usePermissions();
  // const { addNotification } = useNotification();

  // Simulación de hooks para el ejemplo
  const hasPermission = (permission) => true; // Simular 
  const addNotification = (message, type) => {
    logger.info(`Notification: ${message}`, { type });
  };

  // Cargar datos iniciales
  useEffect(() => {
    setTimeout(() => {
      setCompanies(mockCompanies);
      setLoading(false);
    }, 1000);
  }, []);

  // Filtrar empresas
  const filteredCompanies = companies.filter(company => {
    const matchesSearch = company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         company.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || company.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Validar formulario
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'El nombre de la empresa es requerido';
    }
    
    if (!formData.description.trim()) {
      errors.description = 'La descripción es requerida';
    }
    
    if (!formData.adminUser.trim()) {
      errors.adminUser = 'El email del administrador es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminUser)) {
      errors.adminUser = 'El email no tiene un formato válido';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Resetear formulario
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      status: 'active',
      adminUser: ''
    });
    setFormErrors({});
  };

  // Abrir modal para crear
  const openCreateModal = () => {
    resetForm();
    setModalMode('create');
    setSelectedCompany(null);
    setShowModal(true);
  };

  // Abrir modal para editar
  const openEditModal = (company) => {
    setFormData({
      name: company.name,
      description: company.description,
      status: company.status,
      adminUser: company.adminUser
    });
    setFormErrors({});
    setModalMode('edit');
    setSelectedCompany(company);
    setShowModal(true);
  };

  // Abrir modal para ver detalles
  const openViewModal = (company) => {
    setSelectedCompany(company);
    setModalMode('view');
    setShowModal(true);
  };

  // Cerrar modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedCompany(null);
    resetForm();
  };

  // Guardar empresa
  const handleSave = () => {
    if (!validateForm()) return;

    setLoading(true);

    // Simular API call
    setTimeout(() => {
      if (modalMode === 'create') {
        const newCompany = {
          id: Date.now(),
          ...formData,
          createdAt: new Date().toISOString().split('T')[0],
          usersCount: 1,
          templatesCount: 0
        };
        setCompanies(prev => [...prev, newCompany]);
        addNotification('Empresa creada exitosamente', 'success');
      } else if (modalMode === 'edit') {
        setCompanies(prev => prev.map(company => 
          company.id === selectedCompany.id 
            ? { ...company, ...formData }
            : company
        ));
        addNotification('Empresa actualizada exitosamente', 'success');
      }
      
      setLoading(false);
      closeModal();
    }, 1000);
  };

  // Confirmar eliminación
  const confirmDelete = (company) => {
    setCompanyToDelete(company);
    setShowDeleteModal(true);
  };

  // Eliminar empresa
  const handleDelete = () => {
    setLoading(true);
    
    // Simular API call
    setTimeout(() => {
      setCompanies(prev => prev.filter(company => company.id !== companyToDelete.id));
      addNotification('Empresa eliminada exitosamente', 'success');
      setShowDeleteModal(false);
      setCompanyToDelete(null);
      setLoading(false);
    }, 1000);
  };

  // Verificar permisos
  if (!hasPermission('manage_companies')) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center">
          <Building className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Acceso denegado</h3>
          <p className="mt-1 text-sm text-gray-500">
            No tienes permisos para acceder a la gestión de empresas.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Empresas</h1>
          <p className="text-gray-600">Administra las empresas que pueden usar el sistema</p>
        </div>
        {hasPermission('create_company') && (
          <button
            onClick={openCreateModal}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
          >
            <Plus size={20} />
            Nueva Empresa
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Buscar empresas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Filter size={20} className="text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Todos los estados</option>
              <option value="active">Activas</option>
              <option value="inactive">Inactivas</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tabla de empresas */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">Cargando empresas...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Empresa
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Administrador
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usuarios
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Creada
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCompanies.map((company) => (
                  <tr key={company.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{company.name}</div>
                        <div className="text-sm text-gray-500">{company.description}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">{company.adminUser}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        company.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {company.status === 'active' ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {company.usersCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(company.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => openViewModal(company)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="Ver detalles"
                        >
                          <Eye size={16} />
                        </button>
                        {hasPermission('edit_company') && (
                          <button
                            onClick={() => openEditModal(company)}
                            className="text-indigo-600 hover:text-indigo-900 p-1 rounded"
                            title="Editar"
                          >
                            <Edit size={16} />
                          </button>
                        )}
                        {hasPermission('delete_company') && (
                          <button
                            onClick={() => confirmDelete(company)}
                            className="text-red-600 hover:text-red-900 p-1 rounded"
                            title="Eliminar"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {filteredCompanies.length === 0 && (
              <div className="text-center py-8">
                <Building className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No hay empresas</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm || statusFilter !== 'all' 
                    ? 'No se encontraron empresas con los filtros aplicados'
                    : 'Comienza creando tu primera empresa'
                  }
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de formulario */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {modalMode === 'create' && 'Nueva Empresa'}
                {modalMode === 'edit' && 'Editar Empresa'}
                {modalMode === 'view' && 'Detalles de Empresa'}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              {modalMode === 'view' ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Nombre</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedCompany?.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Descripción</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedCompany?.description}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Administrador</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedCompany?.adminUser}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Estado</label>
                    <span className={`mt-1 inline-block px-2 py-1 text-xs font-medium rounded-full ${
                      selectedCompany?.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {selectedCompany?.status === 'active' ? 'Activa' : 'Inactiva'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Usuarios</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedCompany?.usersCount}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Plantillas</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedCompany?.templatesCount}</p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Fecha de creación</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {new Date(selectedCompany?.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Nombre de la empresa *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className={`mt-1 w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        formErrors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Ingrese el nombre de la empresa"
                    />
                    {formErrors.name && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Descripción *
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className={`mt-1 w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        formErrors.description ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Descripción de la empresa"
                    />
                    {formErrors.description && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.description}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Email del administrador *
                    </label>
                    <input
                      type="email"
                      value={formData.adminUser}
                      onChange={(e) => setFormData(prev => ({ ...prev, adminUser: e.target.value }))}
                      className={`mt-1 w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        formErrors.adminUser ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="admin@empresa.com"
                    />
                    {formErrors.adminUser && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.adminUser}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Estado
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="active">Activa</option>
                      <option value="inactive">Inactiva</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {modalMode !== 'view' && (
              <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors duration-200"
                >
                  Cancelar
                </button>
                {((modalMode === 'create' && hasPermission('create_company')) ||
                  (modalMode === 'edit' && hasPermission('edit_company'))) && (
                  <button
                    onClick={handleSave}
                    disabled={loading}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors duration-200 disabled:opacity-50"
                  >
                    {loading ? 'Guardando...' : 'Guardar'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Confirmar eliminación
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  ¿Estás seguro de que quieres eliminar la empresa "{companyToDelete?.name}"? 
                  Esta acción no se puede deshacer y eliminará todos los datos asociados.
                </p>
              </div>
            </div>
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors duration-200"
              >
                Cancelar
              </button>
              {hasPermission('delete_company') && (
                <button
                  onClick={handleDelete}
                  disabled={loading}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors duration-200 disabled:opacity-50"
                >
                  {loading ? 'Eliminando...' : 'Eliminar'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCompaniesModule;